<template>
  <v-dialog
    v-model="dialog"
    @keydown.esc="closeWithCodition"
    persistent
    scrollable
    content-class="medium"
  >
    <v-card>
      <v-card-title>
        <span class="headline">{{ formTitle }}</span>
      </v-card-title>

      <v-tabs v-model="tabSelected" centered>
        <v-tab
          v-for="item in itemsTab"
          :id="item.toLowerCase()"
          :key="item"
          class="font-size-sub"
          v-on:click="currentTab = item"
        >
          {{ item }}
        </v-tab>
      </v-tabs>

      <v-card-text>
        <v-container>
          <v-row v-if="currentTab === 'Info'">
            <v-col cols="12" sm="12" md="12" lg="12">
              <v-text-field
                v-model="editedItem.name"
                label="Grammar name"
                dense
                hide-details
                :rules="[
                  rules.rulesCharcacters50.required,
                  rules.rulesCharcacters50.min,
                  rules.rulesCharcacters50.max
                ]"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="12" lg="12">
              Description
              <v-textarea
                id="description"
                class="textarea-input"
                v-model="editedItem.description"
                rows="4"
                solo
                hide-details
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row v-if="currentTab === 'ASR'">
            <v-col cols="12" sm="12" md="12" lg="12" class="d-flex v-middle">
              <div>ASR engine</div>
              <v-radio-group
                v-model="editedItem.type"
                class="m-l-15 m-t-0"
                dense
                hide-details
                row
              >
                <v-radio label="Google" value="google"></v-radio>
                <v-radio label="VXML" value="vxml"></v-radio>
                <v-radio
                  v-if="!hiddenTrueVoiceAsr"
                  label="True Voice ASR"
                  value="true_voice_asr"
                ></v-radio>
              </v-radio-group>
            </v-col>
            <v-col
              v-if="editedItem.type === 'google'"
              cols="12"
              sm="12"
              md="12"
              lg="12"
            >
              <div class="d-flex">
                <v-select
                  v-model="editedItem.model"
                  :items="models"
                  item-text="label"
                  item-value="value"
                  label="Model"
                  hide-details
                  dense
                ></v-select>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      small
                      class="m-l-5"
                      @click="goToGoogleSpeechModel"
                    >
                      far fa-question-circle
                    </v-icon>
                  </template>
                  <span class="font-size-body">
                    You can improve the recognition result by choosing ASR model
                    to match the audio source.<br /><br />
                    Click to learn more.
                  </span>
                </v-tooltip>
              </div>
              <div
                class="d-flex m-t-20"
                :class="shouldDisableBoost ? 'disabled-div' : ''"
              >
                <div class="input-title">Boost</div>
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-bind="attrs"
                      v-on="on"
                      small
                      class="m-l-5"
                      @click="goToGoogleSpeech"
                    >
                      far fa-question-circle
                    </v-icon>
                  </template>
                  <span class="font-size-body">
                    Higher boost values will increase the likelihood of
                    recognizing these phrases but that also increase the
                    possibility of returning phrases that don’t appear in audio.
                    It is recommended to experiment by adjusting boost value up
                    and down to get the desired recognition result.<br /><br />
                    Click to learn more.
                  </span>
                </v-tooltip>
              </div>
              <div
                class="p-l-15"
                :class="shouldDisableBoost ? 'disabled-div' : ''"
              >
                <div v-for="(google, index) in editedItem.google" :key="index">
                  <div class="d-flex align-start m-t-15">
                    <div class="width100">
                      <v-textarea
                        v-model="editedItem.google[index].str_phrases"
                        :id="`google${index}`"
                        class="textarea-input"
                        placeholder="phrase, phrase, …"
                        solo
                        hide-details
                        @blur="onBlur(index)"
                        rows="3"
                      ></v-textarea>

                      <div class="d-flex align-center justify-end">
                        Boost level
                        <v-slider
                          v-model="editedItem.google[index].boost"
                          class="boost-scroll m-l-5"
                          max="20"
                          min="0"
                          hide-details
                        ></v-slider>
                        <span class="boost-text">
                          {{ editedItem.google[index].boost }}
                        </span>
                      </div>
                    </div>
                    <v-icon
                      small
                      class="m-l-10"
                      @click.prevent="deleteBoost(index)"
                    >
                      fa-times-circle
                    </v-icon>
                  </div>
                </div>

                <div class="d-inline-flex m-t-15" @click="addBoost()">
                  <v-icon class="cursor-pointer p-r-5" small>
                    fa-plus-circle
                  </v-icon>
                  <span class="cursor-pointer">
                    Add boost
                  </span>
                </div>
              </div>
            </v-col>
            <v-col
              v-else-if="editedItem.type === 'vxml'"
              cols="12"
              sm="12"
              md="12"
              lg="12"
            >
              <v-textarea
                v-model="editedItem.vxml"
                class="textarea-input overflow-x"
                placeholder="VXML grammar tag"
                solo
                hide-details
                rows="3"
              ></v-textarea>
            </v-col>
            <v-col
              v-else-if="editedItem.type === 'true_voice_asr'"
              cols="12"
              sm="12"
              md="12"
              lg="12"
            >
              <v-textarea
                v-model="editedItem.address"
                class="textarea-input"
                placeholder="True Voice ASR server address"
                solo
                hide-details
                rows="3"
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row v-if="currentTab === 'Replace text'">
            <v-col cols="12" sm="12" md="12" lg="12">
              <span>Replace part of the recognition before sending to NLU</span>
              <v-tooltip>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" small class="m-l-5">
                    far fa-question-circle
                  </v-icon>
                </template>
                <span v-html="tooltip"></span>
              </v-tooltip>
            </v-col>
            <v-col cols="12" sm="12" md="12" lg="12">
              <v-textarea
                id="replaceText"
                class="textarea-input overflow-x"
                solo
                hide-details
                rows="7"
                v-model="editedItem.replace_text"
                label="<replace word>: <search word>, <search word>, ..."
              ></v-textarea>
            </v-col>
          </v-row>
          <!-- <v-row v-if="currentTab === 'NLU'">
            <v-col cols="12" sm="12" md="12" lg="12">
              <v-select
                v-model="editedItem.ai_model_name"
                :items="nluModels"
                item-text="name"
                item-value="name"
                label="NLU model"
                hide-details
                dense
                clearable
                clear-icon="fa-times"
              ></v-select>
            </v-col>
          </v-row> -->
        </v-container>
      </v-card-text>

      <div id="error" class="red--text text-center">
        {{ errorMessage }}
      </div>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn id="btn-close" class="btn-secondary" @click="close">
          Cancel
        </v-btn>
        <v-btn id="btn-ok" class="btn-primary" @click="save">
          {{ btnSave }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'

import apiAi from '@/apis/ai'

export default {
  name: 'DialogManageQueues',
  props: {
    rules: Object,
    dialog: Boolean,
    editedIndex: Number,
    editedItem: Object,
    errorMessage: String
  },
  computed: {
    shouldDisableBoost() {
      return ['latest_long', 'latest_short'].includes(this.editedItem.model)
    },
    formTitle() {
      return this.editedIndex === -1 ? 'New grammar' : 'Edit grammar'
    },
    btnSave() {
      return this.editedIndex === -1 ? 'Add' : 'Save'
    }
  },
  data() {
    return {
      tabSelected: null,
      currentTab: 'Info',
      itemsTab: ['Info', 'ASR', 'Replace text'],
      tooltip:
        'This can be useful in various scenarios.<br/>' +
        '1) Replace out-of-domain words with in-domain words<br/>' +
        '2) Mask out rude words<br/>' +
        '3) Remove words off the transcription<br/>' +
        '<br/>' +
        'The format is "&lt;replace word&gt;: &lt;search word&gt;, &lt;search word&gt;, ..."<br/>' +
        '<br/>' +
        'Here is an example of a country grammar for the word "Czech"<br/>' +
        'Czech: check, cheque, shake',
      models: [
        {
          label: 'Command and search',
          value: 'command_and_search'
        },
        {
          label: 'Default',
          value: 'default'
        },
        {
          label: 'Latest Long',
          value: 'latest_long'
        },
        {
          label: 'Latest Short',
          value: 'latest_short'
        }
      ],
      // nluModels: [],
      hiddenTrueVoiceAsr: process.env.VUE_APP_HIDDEN_TRUE_VOICE_ASR === 'true'
    }
  },
  async created() {
    for (let i = 0; i < this.editedItem.google.length; i++) {
      if (
        this.editedItem.google[i].phrases &&
        this.editedItem.google[i].phrases.length > 0
      )
        this.editedItem.google[i].str_phrases = this.editedItem.google[
          i
        ].phrases.join(', ')
    }

    // this.getNluModel()
  },
  methods: {
    // async getNluModel() {
    //   try {
    //     this.loading = true

    //     const getModel = await apiAi.getModels()
    //     const { data } = getModel
    //     this.nluModels = data

    //     setTimeout(() => {
    //       this.loading = false
    //     }, 500)
    //   } catch (error) {}
    // },
    onBlur(index) {
      let string = this.editedItem.google[index].str_phrases

      const hasNewLine = string.includes('\n')
      if (hasNewLine) {
        string = string.replace(/\n/g, ',')
      }

      string = string.replace(/\"/g, '')

      const phrases = string
        .split(',')
        .map(item => item.trim())
        .filter(item => item)

      string = phrases.join(', ')

      const googleId = document.getElementById(`google${index}`)
      googleId.value = string

      this.editedItem.google[index].str_phrases = string
    },
    goToGoogleSpeech() {
      window.open(
        'https://cloud.google.com/speech-to-text/docs/adaptation-model'
      )
    },
    goToGoogleSpeechModel() {
      window.open(
        'https://cloud.google.com/speech-to-text/docs/basics#select-model'
      )
    },
    addBoost() {
      this.editedItem.google.push({
        str_phrases: '',
        phrases: [],
        boost: 0
      })
    },
    deleteBoost(index) {
      this.editedItem.google.splice(index, 1)
    },
    closeWithCodition(evt) {
      const id = evt.srcElement.id
      if (id && (id.includes('google') || id === 'replaceText')) return

      this.close()
    },
    close() {
      this.$emit('close', null)
    },
    save() {
      this.$emit('save', null)
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__text {
  height: 400px;
}

.width70px {
  width: 70px;
  max-width: 70px;
}
.width250px {
  width: 250px;
  max-width: 250px;
}

.font-size-body {
  width: 300px;
  position: relative;
  display: flex;
  text-align: justify;
  text-justify: inter-word;
  padding-left: 4px;
  margin-left: 4px;
  padding-right: 4px;
  margin-right: 4px;
  margin-bottom: 0px;
}

.boost-scroll {
  // height: 0px;
  // margin-bottom: 10px;
  width: 150px;
  max-width: 150px;
}

.boost-text {
  width: 20px;
}

.disabled-div {
  pointer-events: none;
  opacity: 0.5;
}
</style>
